import React, { useEffect, useState } from "react";
import {
  Box, Button, Flex, Grid, GridItem, Input, InputGroup, InputLeftElement, Select, Text, Image,
  CloseButton,
} from "@chakra-ui/react";
import MyDiv from "./documentUpload.style";
import UploadLogo from "../../../../../assets/images/UploadLogo.svg";
import { useFetch } from "../../../../../hooks/useFetch";
import { CustomToast } from "../../../../../components";
import { ArrowBackIcon } from "@chakra-ui/icons";

interface DocumentState {
  type: string;
  name: string;
  fileName: string;
  file?: File | null;
  number?: string;
  expiry?: string;
}
interface DocumentUploadProps {
  nextStep: () => void;
  prevStep: () => void;
  employeeDocuments: Record<string, any>;
  setEmployeeDocuments: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  employeeId: number;

}

interface ICategory {
  id: number,
  document_Category_Name: string,
}

interface IName {
  id: number,
  document_Name_Title: string,
  document_Category_Id: number,
  document_Category_Name: string,
  country: number,
  country_Name: string,
  document_Number: boolean,
  expire: boolean,
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ nextStep, prevStep, employeeDocuments, setEmployeeDocuments, employeeId }) => {
  const [loading, setLoading] = useState(false);
  const [dnocumetCategory, setDocumetCategory] = useState<ICategory[]>([]);
  const [dnocumetName, setDocumentName] = useState<{ [key: number]: IName[] }>({});
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const [documents, setDocuments] = useState<DocumentState[]>(
    employeeDocuments.documents || [
      { type: "", name: "", fileName: "", file: null },
      { type: "", name: "", fileName: "", file: null },
      // { type: "", name: "", fileName: "", file: null },
    ]
  );

  const handleTypeChange = async (index: number, value: string) => {
    const updated = [...documents];
    updated[index].type = value;
    updated[index].name = "";
    updated[index].number = "";
    updated[index].expiry = "";
    setDocuments(updated);
    if (value) {
      await LoadDocName(parseInt(value));
    }
  };


  const handleNameChange = (index: number, value: string) => {
    const updated = [...documents];
    updated[index].name = value;
    updated[index].number = "";
    updated[index].expiry = "";
    setDocuments(updated);
  };

  const handleNumberChange = (index: number, value: string) => {
    const updated = [...documents];
    updated[index].number = value;
    setDocuments(updated);
  };

  const handleExpiryChange = (index: number, value: string) => {
    const updated = [...documents];
    updated[index].expiry = value;
    setDocuments(updated);
  };

  // const handleFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   const updated = [...documents];
  //   if (file && file.type === "application/pdf") {
  //     updated[index].fileName = file.name;
  //     updated[index].file = file;
  //   } else {
  //     e.target.value = "";
  //     updated[index].fileName = "";
  //     updated[index].file = null;
  //   }
  //   setDocuments(updated);
  // };

  const handleFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const updated = [...documents];

    if (file) {
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];

      if (!allowedTypes.includes(file.type)) {
        addToast({ message: "Only PDF or image files (JPG, PNG) are allowed.", status: "error" });
        e.target.value = "";
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5 MB
        addToast({ message: "File size should not exceed 5 MB.", status: "error" });
        e.target.value = "";
        return;
      }
      updated[index].fileName = file.name;
      updated[index].file = file;
    } else {
      updated[index].fileName = "";
      updated[index].file = null;
    }
    setDocuments(updated);
  };

  const renderInputs = (doc: DocumentState) => {
    const selectedName = dnocumetName[parseInt(doc.type)]?.find(n => n.id === parseInt(doc.name));
    if (!selectedName) return null;

    return (
      <>
        {selectedName.document_Number && (
          <div>
            <label className="font-poppins text_sm text_regular">Document Number</label>
            <Input
              placeholder="Enter Document Number"
              mb={3}
              value={doc.number || ""}
              onChange={(e) =>
                handleNumberChange(documents.indexOf(doc), e.target.value)
              }
              className="font-poppins text_sm text_regular"
            />
          </div>
        )}

        {selectedName.expire && (
          <div>
            <label className="font-poppins text_sm text_regular">Expiry Date</label>
            <Input
              type="date"
              value={doc.expiry || ""}
              onChange={(e) =>
                handleExpiryChange(documents.indexOf(doc), e.target.value)
              }
              className="font-poppins text_sm text_regular"
            />
          </div>
        )}
      </>
    );
  };

  const handleNextClick = async () => {
    const errors: string[] = [];

    //  At least one document uploaded check
    const hasUploadedFile = documents.some((doc) => doc.type && doc.name && doc.file);
    if (!hasUploadedFile) {
      addToast({ message: "Please upload at least one document before proceeding.", status: "error", });
      return;
    }

    //  Duplicate check (Category + Name)
    const duplicateCheck = new Set<string>();
    documents.forEach((doc, i) => {
      if (doc.type && doc.name) {
        const key = `${doc.type}-${doc.name}`;
        if (duplicateCheck.has(key)) {
          errors.push(`Duplicate document found at Document ${i + 1}. Please remove or change it.`);
        } else {
          duplicateCheck.add(key);
        }
      }
    });

    //  Basic required checks (Name, File, File Size)
    documents.forEach((doc, i) => {
      if (doc.type && !doc.name) errors.push(`Document ${i + 1}: Document Name is required.`);
      if (doc.type && !doc.file) errors.push(`Document ${i + 1}: File upload is required.`);

      if (doc.file && doc.file.size > 5 * 1024 * 1024) {
        errors.push(`Document ${i + 1}: File size must not exceed 5 MB.`);
      }
    });

    //  Extra validations (Number / Expiry)
    documents.forEach((doc, i) => {
      const selectedName = dnocumetName[parseInt(doc.type)]?.find((n) => n.id === parseInt(doc.name));
      if (selectedName?.document_Number && !doc.number) {
        errors.push(`Document ${i + 1}: Document Number is required.`);
      }
      if (selectedName?.expire && !doc.expiry) {
        errors.push(`Document ${i + 1}: Expiry Date is required.`);
      }
    });

    if (errors.length > 0) {
      errors.forEach((msg, index) => {
        setTimeout(() => {
          addToast({ message: msg, status: "error" });
        }, index * 200);
      });
      return;
    }

    try {
      setLoading(true);
      const formDataPayload = new FormData();

      if (!employeeId) { addToast({ message: "Employee ID not found", status: "error" }); return; }

      formDataPayload.append("employeeId", employeeId.toString());

      // ✅ Append all valid documents
      documents.forEach((doc, i) => {
        if (doc.type && doc.name && doc.file) {
          formDataPayload.append(`documents[${i}].documentCategoryId`, doc.type);
          formDataPayload.append(`documents[${i}].documentNameId`, doc.name);
          formDataPayload.append(`documents[${i}].documentNumber`, doc.number || "");
          formDataPayload.append(`documents[${i}].expiryDate`, doc.expiry || "");
          formDataPayload.append(`documents[${i}].file`, doc.file);
        }
      });

      const response = await fetchApi("Hrlense_Employee/DocumentUpload", "POST", formDataPayload, null, "Documents uploaded successfully", true);
      if (response) nextStep();
    } catch (error) {
      addToast({ message: `Error uploading documents: ${error instanceof Error ? error.message : "Unknown error"}`, status: "error", });
    } finally {
      setLoading(false);
    }
  };


  const LoadDocName = async (category_Id: number) => {
    const response = await fetchApi("DropDown/DocumentName", "GET", null, `category_Id=${category_Id}`);
    if (response) {
      setDocumentName((prev) => ({ ...prev, [category_Id]: response }));
    }
  };

  const LoadDocCategory = async () => {
    const response = await fetchApi("Hrlense_Document_Category", "GET", null, null);
    if (response) {
      setDocumetCategory(response);
    }
  };

  useEffect(() => {
    LoadDocCategory()
  }, []);

  useEffect(() => {
    documents.forEach((doc) => {
      if (doc.type && !dnocumetName[parseInt(doc.type)]) {
        LoadDocName(parseInt(doc.type));
      }
    })
  }, [documents]);

  useEffect(() => {
    setEmployeeDocuments((prev) => ({ ...prev, documents }));
  }, [documents, setEmployeeDocuments]);

  // Add new function for adding document
  const handleAddDocument = () => {
    setDocuments((prev) => [
      ...prev,
      { type: "", name: "", fileName: "", file: null }
    ]);
  };

  // Remove specific document
  const handleRemoveDocument = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };


  return (
    <MyDiv>
      <Box m={6}>
        <Grid className="grid_container">
          <GridItem colSpan={6}>
            {documents.map((doc, i) => (
              <Box py={2} key={i} borderBottom="1px solid #eee" mb={4}>
                <Flex justify="space-between" align="center">
                  <Text className="font-poppins text_md text_medium" mb={2}>
                    Upload Document {i + 1}
                  </Text>

                  {documents.length > 1 && (
                    <CloseButton
                      size="sm"
                      color="red.500"
                      onClick={() => handleRemoveDocument(i)}
                    />
                  )}
                </Flex>

                <Flex gap={4} mb={4}>
                  <Box>
                    <Select
                      placeholder="Select Document Type"
                      value={doc.type}
                      onChange={(e) => handleTypeChange(i, e.target.value)}
                      className="font-poppins text_sm text_regular"
                    >
                      {dnocumetCategory.map((type: ICategory) => (
                        <option key={type.id} value={type.id}>
                          {type.document_Category_Name}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box>
                    <Select
                      placeholder="Select Document Name"
                      value={doc.name}
                      onChange={(e) => handleNameChange(i, e.target.value)}
                      className="font-poppins text_sm text_regular"
                      isDisabled={!doc.type}
                    >
                      {(dnocumetName[parseInt(doc.type)] || []).map((name) => (
                        <option key={name.id} value={name.id}>
                          {name.document_Name_Title}
                        </option>
                      ))}
                    </Select>
                  </Box>
                </Flex>

                <Box mb={4}>{renderInputs(doc)}</Box>

                <Flex align="center" mb={4}>
                  <InputGroup>
                    <InputLeftElement width="auto" px={2}>
                      <Button
                        as="label"
                        htmlFor={`file-upload-${i}`}
                        size="sm"
                        colorScheme="gray"
                        leftIcon={<Image src={UploadLogo} />}
                        cursor="pointer"
                        borderRadius="md"
                        w="100%"
                        className="font-poppins text_sm text_regular"
                      >
                        Upload
                      </Button>
                      <input
                        id={`file-upload-${i}`}
                        type="file"
                        accept="application/pdf,image/png,image/jpeg,image/jpg"
                        hidden
                        onChange={(e) => handleFileChange(i, e)}
                      />
                    </InputLeftElement>

                    <Input
                      className="font-poppins text_sm text_regular"
                      placeholder="No file chosen"
                      value={doc.fileName}
                      isReadOnly
                      pl="120px"
                    />
                  </InputGroup>
                </Flex>
              </Box>
            ))}

            <Box mt={4}>
              <Button size="sm" colorScheme="green" onClick={handleAddDocument}>
                + Add More Document
              </Button>
            </Box>

          </GridItem>
        </Grid>
        <Box mt={6} display="flex" justifyContent="space-between">
          <Button onClick={prevStep} colorScheme="gray" leftIcon={<ArrowBackIcon boxSize={4} />}>Previous</Button>
          <Button onClick={handleNextClick} isLoading={loading} colorScheme="green" >Submit</Button>
        </Box>
      </Box>
    </MyDiv>
  );
};

export default DocumentUpload;
